import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import ReplaceElementModalContext, {
  ModalStep,
  View,
} from '@atom/components/common/replaceElement/ReplaceElementModalContext';
import { GET_ASSET, MOVE_ELEMENT, REPLACE_ELEMENT } from '@atom/graph/asset';
import { Button, Icon, IconButton, Modal, Progress, Snackbar } from '@atom/mui';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  AssetDetail,
  ElementError,
  SearchAssetWithElements,
} from '@atom/types/asset';
import { CategoryPathItem } from '@atom/types/category';
import { InventoryAssetDetailType } from '@atom/types/inventory';
import { getErrorCode } from '@atom/utilities/validationUtilities';

import ConfirmStep from './ConfirmStep';
import DestinationStep from './DestinationStep';
import ErrorView from './ErrorView';
import {
  getElementErrorsFromError,
  getModalStepsArray,
  isCurrentStepDisabled,
} from './replaceElementUtilities';
import ReplacementStep from './ReplacementStep';
import StepperSidebar from './StepperSidebar';
import SuccessView from './SuccessView';

import classes from './replaceElement.module.css';

const styles = {
  modalContent: {
    padding: 0,
  },
  back: {
    marginRight: '1rem',
  },
};

const MODAL_SIZES = {
  [View.MAIN]: '50rem',
  [View.ERROR]: '30rem',
  [View.SUCCESS]: '30rem',
};

const CONTENT_STYLES = {
  [View.MAIN]: { height: '37.5rem' },
  [View.ERROR]: { height: '37.5rem' },
  [View.SUCCESS]: { height: 'auto' },
};

interface Props {
  open: boolean;
  onClose: () => void;
  elementId: any;
  rootAsset: AssetDetail;
  // Parent of the current element, could be either root asset or another element
  parentSchemaId: string;
}

const ReplaceElementModal = ({
  open,
  onClose,
  elementId,
  rootAsset,
  parentSchemaId,
}: Props) => {
  const [activeView, setActiveView] = useState<View>(View.MAIN);
  const [activeStep, setActiveStep] = useState<ModalStep>(ModalStep.REPLACE);
  const [element, setElement] = useState<AssetDetail>();
  const [replacementAsset, setReplacementAsset] = useState<
    InventoryAssetDetailType | SearchAssetWithElements
  >();
  const [replacementAssetPath, setReplacementAssetPath] = useState<
    CategoryPathItem[]
  >([]);
  const [destinationAsset, setDestinationAsset] = useState<
    InventoryAssetDetailType | SearchAssetWithElements
  >();
  const [destinationAssetPath, setDestinationAssetPath] = useState<
    CategoryPathItem[]
  >([]);
  const [elementErrors, setElementErrors] = useState<{
    [elementId: string]: ElementError;
  }>();

  const [getAsset, { loading: elementLoading }] = useLazyQuery(GET_ASSET, {
    fetchPolicy: 'no-cache',
  });

  const [moveElement, { loading: moveElementLoading }] =
    useMutation(MOVE_ELEMENT);

  const [replaceElement, { loading: replaceElementLoading }] =
    useMutation(REPLACE_ELEMENT);

  const resetValues = () => {
    setActiveStep(ModalStep.REPLACE);
    setActiveView(View.MAIN);
    setDestinationAsset(null);
    setDestinationAssetPath([]);
    setReplacementAsset(null);
    setReplacementAssetPath([]);
    setElementErrors(null);
    setElement(null);
  };

  const onCloseModal = () => {
    resetValues();
    onClose();
  };

  const validateElement = async (id: string) => {
    try {
      await moveElement({
        variables: { input: { elementId: id, validateOnly: true } },
      });
    } catch (error) {
      if (getErrorCode(error) === 422) {
        const elementErrorData = getElementErrorsFromError(error);

        setElementErrors(elementErrorData);
        setActiveView(View.ERROR);
      } else {
        Snackbar.error({ message: 'An error occurred. Please try again.' });
      }
    }
  };

  useEffect(() => {
    const getAssetRequest = async () => {
      const { data } = await getAsset({ variables: { id: elementId } });
      setElement(data?.asset);
    };

    if (open) {
      getAssetRequest();
      validateElement(elementId);
    }
  }, [open]);

  useEffect(() => {
    if (activeStep === ModalStep.REPLACE) {
      setReplacementAsset(null);
      setReplacementAssetPath([]);
    } else if (activeStep === ModalStep.DESTINATION) {
      setDestinationAsset(null);
      setDestinationAssetPath([]);
    }
  }, [activeStep]);

  const STEPS_ARRAY = getModalStepsArray();

  const isConfirmDisabled = isCurrentStepDisabled({
    STEPS_ARRAY,
    activeStep,
    element,
    replacementAsset,
    destinationAsset,
  });

  const confirm = async () => {
    try {
      await replaceElement({
        variables: {
          input: {
            elementId: element.id,
            destinationParentAssetId: destinationAsset.id,
            replacementElementId: replacementAsset.id,
          },
        },
      });

      setActiveView(View.SUCCESS);
    } catch (error) {
      if (getErrorCode(error) === 422) {
        const elementErrorData = getElementErrorsFromError(error);

        setElementErrors(elementErrorData);
        setActiveView(View.ERROR);
      } else {
        Snackbar.error({ message: 'An error occurred. Please try again.' });
      }
    }
  };

  const handleStep = (value: number) => {
    const currentStepIndex = R.findIndex(R.propEq(activeStep, 'value'))(
      STEPS_ARRAY,
    );

    const newStep = STEPS_ARRAY[currentStepIndex + value];

    setActiveStep(newStep.value);
  };

  const handleReplacementAssetClick = (
    asset: InventoryAssetDetailType | SearchAssetWithElements,
    path: CategoryPathItem[],
  ) => {
    validateElement(asset?.id);
    handleStep(1);
    setReplacementAsset(asset);
    setReplacementAssetPath(path);
  };

  const handleDestinationAssetClick = (
    asset: InventoryAssetDetailType | SearchAssetWithElements,
    path: CategoryPathItem[],
  ) => {
    handleStep(1);
    setDestinationAsset(asset);
    setDestinationAssetPath(path);
  };

  const getStepContent = (): React.ReactNode => {
    const stepContent = {
      [ModalStep.ELEMENT]: <div />,
      [ModalStep.REPLACE]: (
        <ReplacementStep
          handleReplacementAssetClick={handleReplacementAssetClick}
        />
      ),
      [ModalStep.DESTINATION]: (
        <DestinationStep
          handleDestinationAssetClick={handleDestinationAssetClick}
        />
      ),
      [ModalStep.CONFIRM]: <ConfirmStep />,
    };

    return stepContent[activeStep];
  };

  const getTitle = () => {
    const titles = {
      [View.MAIN]: (
        <div className={classes.modalHeader}>
          <div>Replace Sub Item</div>
          <IconButton
            onClick={onClose}
            data-cy="replaceElementModalCloseButton"
          >
            <Icon icon={CloseIcon} />
          </IconButton>
        </div>
      ),
      [View.ERROR]: (
        <div className={classes.modalHeader}>Cannot Move/Replace Sub Item</div>
      ),
      [View.SUCCESS]: <div />,
    };

    return titles[activeView];
  };

  const confirmError = () => {
    if (activeStep === ModalStep.REPLACE) {
      onCloseModal();
    } else if (activeStep === ModalStep.DESTINATION) {
      setActiveView(View.MAIN);
      setActiveStep(ModalStep.REPLACE);
    } else {
      setActiveView(View.MAIN);
    }
  };

  const getStepFooter = () => {
    const showCreateButton =
      R.findIndex(R.propEq(activeStep, 'value'), STEPS_ARRAY) ===
      STEPS_ARRAY.length - 1;
    const showBackButton = activeStep !== ModalStep.REPLACE;

    const isDisabled = isConfirmDisabled;

    const rightArrowColor = isDisabled
      ? colors.neutral.gray
      : colors.neutral.white;

    return (
      <div className={classes.footer}>
        {showBackButton ? (
          <Button onClick={() => handleStep(-1)} data-cy="replaceElementBack">
            <Icon icon={ChevronLeftIcon} />
            Back
          </Button>
        ) : (
          <div />
        )}
        <div>
          <Button
            onClick={onCloseModal}
            style={{ marginRight: '0.5rem' }}
            data-cy="replaceElementModalCancelButton"
          >
            Cancel
          </Button>
          {showCreateButton ? (
            <Button
              color="primary"
              variant="contained"
              onClick={confirm}
              disabled={isDisabled}
              data-cy="replaceElementModalCreateButton"
            >
              Confirm & Replace
            </Button>
          ) : (
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleStep(1)}
              disabled={isDisabled}
              data-cy="replaceElementModalNextButton"
            >
              Next
              <Icon color={rightArrowColor} icon={ChevronRightIcon} />
            </Button>
          )}
        </div>
      </div>
    );
  };

  const getErrorFooter = () => {
    return (
      <div className={classes.footer}>
        <div />
        <Button
          color="primary"
          variant="contained"
          onClick={confirmError}
          data-cy="replaceElementModalErrorOkButton"
        >
          OK
        </Button>
      </div>
    );
  };

  const getFooter = () => {
    const footers = {
      [View.MAIN]: getStepFooter(),
      [View.ERROR]: getErrorFooter(),
      [View.SUCCESS]: <div />,
    };

    return footers[activeView];
  };

  const title = getTitle();
  const footer = getFooter();

  const content = useMemo((): React.ReactNode => {
    const stepContent = getStepContent();

    const views = {
      [View.MAIN]: (
        <>
          <StepperSidebar />
          {stepContent}
        </>
      ),
      [View.ERROR]: <ErrorView />,
      [View.SUCCESS]: <SuccessView onCloseModal={onCloseModal} />,
    };

    return views[activeView];
  }, [activeView, activeStep]);

  const modalWidth = MODAL_SIZES[activeView];
  const contentStyle = CONTENT_STYLES[activeView];

  const loading =
    elementLoading ||
    R.isEmpty(element) ||
    moveElementLoading ||
    replaceElementLoading;

  return (
    <ReplaceElementModalContext.Provider
      value={{
        activeStep,
        setActiveStep,
        STEPS_ARRAY,
        parentSchemaId,
        rootAsset,
        element,
        replacementAsset,
        replacementAssetPath,
        destinationAsset,
        destinationAssetPath,
        elementErrors,
      }}
    >
      <Modal
        title={title}
        disableHeader={activeView === View.SUCCESS}
        footer={footer}
        disableFooter={activeView === View.SUCCESS}
        open={open}
        contentStyle={styles.modalContent}
        width={modalWidth}
        loading={loading}
        disableCloseOnOutsideClick={activeView !== View.SUCCESS}
        onCancel={onCloseModal}
        data-cy="replaceElementModal"
      >
        <div className={classes.modalContent} style={contentStyle}>
          {loading ? (
            <div className={classes.spinnerContainer}>
              <Progress />
            </div>
          ) : (
            content
          )}
        </div>
      </Modal>
    </ReplaceElementModalContext.Provider>
  );
};

export default ReplaceElementModal;
